import { services } from '@config/services';
import type { ShippingMappingEntry } from '@features/merchant/api/types';
import { vendorApi, type FacilityAddress, type FacilityContact } from './_base';

/**
 * Facility endpoints — sub-resource of vendor
 * (`/v1/vendors/{vendorId}/facilities*`).
 *
 * Facility detail fetch, replace-semantics update, and create-new.
 * The facility's shipping mappings (carrier × method × countries)
 * round-trip through `updateFacility` because the server uses
 * replace semantics — there's no per-mapping endpoint.
 */

/** Server-derived geocoded coordinates. Read-only — never sent on write. */
export interface FacilityLocation {
  latitude: number;
  longitude: number;
}

/**
 * Address as returned by the server (GET/PUT response). Extends the editable
 * {@link FacilityAddress} with the server-derived geocoding result. These extra
 * fields are read-only and must never be added to the write payload.
 */
export interface FacilityResponseAddress extends FacilityAddress {
  location?: FacilityLocation;
  /** True when geocoding resolved coordinates for the address. */
  locationResolved?: boolean;
}

/** Optional contact name printed on the facility's return address. The
 * facility's postal address is the return address itself — this only
 * carries the person name shown above it. */
export interface FacilityReturnAddress {
  firstName?: string;
  lastName?: string;
}

export interface FacilityDetails {
  id: string;
  facilityId?: string;
  shortId?: string;
  name: string;
  contacts?: FacilityContact[];
  address?: FacilityResponseAddress;
  /** Vendor-specific routing code (e.g. DistrictPhoto, Harrier). */
  facilityCodeId?: string | null;
  returnAddress?: FacilityReturnAddress | null;
  /** Active flag — when true the facility is unavailable. */
  disabled?: boolean;
  /** Selected value-added services for this facility. */
  vasOptions?: string[];
  /** Shipping mappings stored on the facility record (carrier × method × countries). */
  shippingMappings?: ShippingMapping[];
}

/** Vendor↔carrier code pair stored on a detailed service method. */
export interface ShippingMappingVendorCode {
  carrierCode?: string;
  serviceCode?: string;
  [key: string]: unknown;
}

/** One carrier-service entry under a detailed mapping's shipment method. */
export interface ShippingServiceMethod {
  serviceMethodsAllowed?: string[];
  /** Country codes this service applies to ('ROW' = Rest of World). */
  countryCodesToApply?: string[];
  vendorMapping?: ShippingMappingVendorCode;
  [key: string]: unknown;
}

/**
 * One row in `facility.shippingMappings`. Shape verbatim from the legacy
 * record. Mutations send the FULL facility back via `updateFacility`
 * (replace semantics) — there's no per-mapping endpoint.
 *
 * Two flavors discriminated by `type`:
 * - `Detailed` — `carrierName` + `shippingMethods[].carrierShippingServiceMethods[]`
 *   (each carrying `serviceMethodsAllowed`, `countryCodesToApply`, `vendorMapping`).
 * - `Basic` — `shippingMethods[].vendorShippingMethod` only.
 */
export interface ShippingMapping {
  type?: 'Detailed' | 'Basic';
  carrierId?: string;
  carrierName?: string;
  shippingMethods?: ShippingMappingMethod[];
  /** Legacy top-level countries (older simple shape); detailed mappings use
   * `countryCodesToApply` on each service method instead. */
  countries?: string[];
  isDefault?: boolean;
  [key: string]: unknown;
}

export interface ShippingMappingMethod {
  shipmentMethod?: string;
  /** Basic mappings: the vendor's own name for this system method. */
  vendorShippingMethod?: string;
  /** Detailed mappings: nested carrier-service entries. */
  carrierShippingServiceMethods?: ShippingServiceMethod[];
  [key: string]: unknown;
}

interface FacilityDetailsEnvelope {
  facility?: FacilityDetails;
}

/** Sub-facility belonging to a vendor (subset used by FacilitiesView). */
export interface VendorFacility {
  id: string;
  name?: string;
  disabled?: boolean;
  address?: FacilityAddress;
  contacts?: FacilityContact[];
  facilityCodeId?: string | null;
  /**
   * Vendor's DEFAULT shipping mapping per facility. Drives the
   * "Vendor's Shipping Mapping" read-only table on a plugin's
   * Shipping Mapping sub-tab — same shape as the merchant override
   * (see `ShippingMappingEntry` in merchant/api/types), differing
   * only in source.
   */
  shippingMappings?: ShippingMappingEntry[];
}

/** Form payload accepted by addFacility (POST). The server assigns the id. */
export interface AddFacilityInput {
  name: string;
  address: FacilityAddress;
  contacts?: FacilityContact[];
  facilityCodeId?: string | null;
  returnAddress?: FacilityReturnAddress | null;
}

const facilitiesApi = vendorApi.injectEndpoints({
  endpoints: (builder) => ({
    /**
     * GET /vendor/v1/vendors/{vendorId}/facilities/{facilityId}
     * Response is wrapped: `{ facility: {...} }`. We unwrap so callers get the
     * facility directly.
     */
    getFacilityDetails: builder.query<
      FacilityDetails | undefined,
      { vendorId: string; facilityId: string }
    >({
      query: ({ vendorId, facilityId }) => ({
        url: `${services.vendor}/v1/vendors/${vendorId}/facilities/${facilityId}`,
        method: 'GET',
      }),
      transformResponse: (response: FacilityDetailsEnvelope | FacilityDetails) => {
        if (response && typeof response === 'object' && 'facility' in response) {
          return (response as FacilityDetailsEnvelope).facility;
        }
        return response as FacilityDetails;
      },
      providesTags: (_r, _e, { vendorId, facilityId }) => [
        { type: 'Facility', id: `${vendorId}:${facilityId}` },
      ],
    }),
    /**
     * PUT /vendor/v1/vendors/{vendorId}/facilities/{facilityId} — server has
     * replace semantics, so the data shape must be the full facility record
     * (including vasOptions, shippingMappings, contacts, etc.). Accepts
     * either the trimmed list-row shape (`VendorFacility`) or the full
     * detail shape (`FacilityDetails`) since callers vary by surface.
     */
    updateFacility: builder.mutation<
      void,
      {
        vendorId: string;
        facilityId: string;
        data: VendorFacility | FacilityDetails;
      }
    >({
      query: ({ vendorId, facilityId, data }) => ({
        url: `${services.vendor}/v1/vendors/${vendorId}/facilities/${facilityId}`,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: (_r, _e, { vendorId, facilityId }) => [
        { type: 'Facility', id: `${vendorId}:${facilityId}` },
        { type: 'Vendor', id: vendorId },
      ],
    }),
    /**
     * POST /vendor/v1/vendors/{vendorId}/facilities — add a new facility to
     * an existing vendor. Server assigns the facility id. Invalidates the
     * parent vendor's cache so FacilitiesView refetches with the new row.
     */
    addFacility: builder.mutation<
      VendorFacility,
      { vendorId: string; data: AddFacilityInput }
    >({
      query: ({ vendorId, data }) => ({
        url: `${services.vendor}/v1/vendors/${vendorId}/facilities`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: (_r, _e, { vendorId }) => [{ type: 'Vendor', id: vendorId }],
    }),
  }),
});

export const {
  useGetFacilityDetailsQuery,
  useUpdateFacilityMutation,
  useAddFacilityMutation,
} = facilitiesApi;
